import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { superAdminApi } from '../../services/superAdminApi';

const SuperAdminDashboard = () => {
  const { user } = useSuperAdminAuth();
  const [stats, setStats] = useState(null);
  const [domainStats, setDomainStats] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentDomains, setRecentDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get current date formatted
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [statsRes, domainStatsRes, requestsRes, domainsRes] = await Promise.all([
        superAdminApi.getDashboardStats(),
        superAdminApi.getDomainStats(),
        superAdminApi.getSignupRequests({ status: 'pending', per_page: 5 }),
        superAdminApi.getDomains({ per_page: 5 })
      ]);

      // Handle different response structures
      setStats(statsRes.data.stats || statsRes.data.data?.stats);
      setDomainStats(domainStatsRes.data.data || domainStatsRes.data);
      setRecentRequests(requestsRes.data.data?.signup_requests || requestsRes.data.signup_requests || []);
      setRecentDomains(domainsRes.data.data?.domains || domainsRes.data.domains || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="super-admin-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard Overview</h1>
          <p>Welcome back, {user?.name}! Here's what's happening today.</p>
        </div>
        <div className="header-date">
          <div className="date-label">Today</div>
          <div className="date-value">{currentDate}</div>
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <i className="fas fa-building stat-icon"></i>
          {loading ? (
            <div className="stat-value loading-shimmer">-</div>
          ) : (
            <div className="stat-value">{stats?.total_tenants || 0}</div>
          )}
          <div className="stat-label">Total Tenants</div>
          <div className="stat-trend">
            <i className="fas fa-arrow-up"></i> {stats?.growth?.new_tenants || 0} new this month
          </div>
        </div>

        <div className="stat-card">
          <i className="fas fa-clock stat-icon"></i>
          {loading ? (
            <div className="stat-value loading-shimmer">-</div>
          ) : (
            <div className="stat-value">{stats?.pending_requests || 0}</div>
          )}
          <div className="stat-label">Pending Requests</div>
          <div className="stat-trend">
            {stats?.pending_requests === 0 ? (
              <><i className="fas fa-check"></i> All processed</>
            ) : (
              <><i className="fas fa-exclamation-circle"></i> Needs attention</>
            )}
          </div>
        </div>

        <div className="stat-card">
          <i className="fas fa-globe stat-icon"></i>
          {loading ? (
            <div className="stat-value loading-shimmer">-</div>
          ) : (
            <div className="stat-value">{domainStats?.active_domains || 0}</div>
          )}
          <div className="stat-label">Active Domains</div>
          <div className="stat-trend">
            <i className="fas fa-shield-alt"></i> {domainStats?.ssl_enabled_count || 0} SSL secured
          </div>
        </div>

        <div className="stat-card">
          <i className="fas fa-server stat-icon"></i>
          {loading ? (
            <div className="stat-value loading-shimmer">-</div>
          ) : (
            <div className="stat-value">{stats?.active_tenants || 0}</div>
          )}
          <div className="stat-label">Active Clients</div>
          <div className="stat-trend">
            <i className="fas fa-check-circle"></i> {stats?.suspended_tenants || 0} suspended
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="content-card">
          <h3>Recent Tenant Requests</h3>
          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading...</p>
            </div>
          ) : recentRequests.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <p>No pending requests</p>
            </div>
          ) : (
            recentRequests.map((request) => (
              <div className="list-item" key={request.id}>
                <div className="item-info">
                  <div className="item-title">{request.organization_name}</div>
                  <div className="item-subtitle">
                    <i className="fas fa-envelope"></i> {request.email}
                  </div>
                </div>
                <span className={`item-status ${request.status}`}>
                  {request.status}
                </span>
              </div>
            ))
          )}
          <Link to="/clients" className="view-all-link">
            View All Clients <i className="fas fa-arrow-right"></i>
          </Link>
        </div>

        <div className="content-card">
          <h3>Domain Status</h3>
          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading...</p>
            </div>
          ) : recentDomains.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-globe"></i>
              <p>No domains configured</p>
            </div>
          ) : (
            recentDomains.slice(0, 3).map((domain) => (
              <div className="list-item" key={domain.id}>
                <div className="item-info">
                  <div className="item-title">
                    {domain.tenant?.name || 'Unknown Tenant'}
                    {domain.is_primary && <span className="primary-tag">Primary</span>}
                  </div>
                  <div className="item-subtitle">
                    <i className="fas fa-link"></i> {domain.full_domain || domain.domain_name}
                  </div>
                </div>
                <span className={`item-status ${domain.status}`}>
                  {domain.status}
                </span>
              </div>
            ))
          )}
          <Link to="/domains" className="view-all-link">
            View All Domains <i className="fas fa-arrow-right"></i>
          </Link>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="stats-summary">
        <div className="summary-card">
          <div className="summary-header">
            <i className="fas fa-chart-pie"></i>
            <h4>Request Statistics</h4>
          </div>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-value">{stats?.total_requests || 0}</span>
              <span className="summary-label">Total Requests</span>
            </div>
            <div className="summary-item">
              <span className="summary-value text-success">{stats?.approved_requests || 0}</span>
              <span className="summary-label">Approved</span>
            </div>
            <div className="summary-item">
              <span className="summary-value text-warning">{stats?.pending_requests || 0}</span>
              <span className="summary-label">Pending</span>
            </div>
            <div className="summary-item">
              <span className="summary-value text-danger">{stats?.rejected_requests || 0}</span>
              <span className="summary-label">Rejected</span>
            </div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-header">
            <i className="fas fa-globe-americas"></i>
            <h4>Domain Statistics</h4>
          </div>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-value">{domainStats?.total_domains || 0}</span>
              <span className="summary-label">Total Domains</span>
            </div>
            <div className="summary-item">
              <span className="summary-value">{domainStats?.subdomain_count || 0}</span>
              <span className="summary-label">Subdomains</span>
            </div>
            <div className="summary-item">
              <span className="summary-value">{domainStats?.custom_domain_count || 0}</span>
              <span className="summary-label">Custom Domains</span>
            </div>
            <div className="summary-item">
              <span className="summary-value text-warning">{domainStats?.ssl_expiring_count || 0}</span>
              <span className="summary-label">SSL Expiring</span>
            </div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <Link to="/clients" className="action-btn">
            <i className="fas fa-user-plus"></i>
            <span>Add New Client</span>
          </Link>
          <Link to="/domains" className="action-btn">
            <i className="fas fa-plus-circle"></i>
            <span>Add Domain</span>
          </Link>
          <Link to="/clients" className="action-btn">
            <i className="fas fa-tasks"></i>
            <span>View Requests ({stats?.pending_requests || 0})</span>
          </Link>
          <Link to="/domains" className="action-btn">
            <i className="fas fa-cog"></i>
            <span>Manage Domains</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
